import React, { useState } from 'react';
import { Heart, Clock, Leaf, ExternalLink } from 'lucide-react';
import { menuItems } from '../data/menuData';
import { MenuItem } from '../types';

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState<'pizza' | 'pasta' | 'desserts' | 'salads' | 'drinks' | 'appetizers' | 'calzones'>('pizza');

  const menuCategories = [
    { 
      id: 'appetizers' as const, 
      name: 'APPETIZERS', 
      image: '/HotSlicePizza_ChickenWings.jpg'
    },
    { 
      id: 'pizza' as const, 
      name: 'PIZZA', 
      image: '/classic-cheese-pizza.jpg'
    },
    { 
      id: 'calzones' as const, 
      name: 'CALZONES', 
      image: '/bbq-chicken-calzone.jpg'
    },
    { 
      id: 'pasta' as const, 
      name: 'PASTA', 
      image: '/rigatoni-bolognese.jpg'
    },
    { 
      id: 'salads' as const, 
      name: 'SALADS', 
      image: '/HotSlicePizza_HouseSalad.jpg'
    },
    { 
      id: 'desserts' as const, 
      name: 'DESSERTS', 
      image: '/HotSlicePizza_Gelato.jpg'
    },
    { 
      id: 'drinks' as const, 
      name: 'DRINKS', 
      image: 'https://images.pexels.com/photos/50593/coca-cola-cold-drink-soft-drink-coke-50593.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop'
    }
  ];

  const filteredItems = menuItems.filter(item => item.category === activeCategory);

  const handleOrderNow = () => {
    window.open('https://order.toasttab.com/online/hot-slice-pizza-41-dravus-street', '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="menu" className="py-12 sm:py-20 bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-600 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-yellow-600 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Our <span className="text-red-600">Menu</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto px-4 mb-8">
            Discover authentic NY style flavors crafted with the finest ingredients and traditional recipes.
          </p>
          
          {/* Special Offers Banner */}
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* $10 Combo Offer */}
              <div className="relative group bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative p-4 sm:p-6 text-center">
                  <div className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3 inline-block">
                    SPECIAL OFFER
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">$10 Combo Deal</h3>
                  <p className="text-orange-100 text-sm mb-3">2 Pizza Slices + Soda</p>
                </div>
              </div>
              
              {/* SPU Student Discount */}
              <div className="relative group bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative p-4 sm:p-6 text-center">
                  <div className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3 inline-block">
                    STUDENT DISCOUNT
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">SPU Students</h3>
                  <p className="text-blue-100 text-sm mb-3">10% Off with Valid ID</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Order Now Button */}
          <button
            onClick={handleOrderNow}
            className="inline-flex items-center space-x-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
          >
            <span>Order Now</span>
            <ExternalLink className="h-5 w-5" />
          </button>
        </div>

        {/* Compact Category Selector */}
        <div className="flex justify-center mb-12 sm:mb-16 px-4">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-6 max-w-6xl">
            {menuCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`group relative flex flex-col items-center transition-all duration-500 transform hover:scale-105 ${
                  activeCategory === category.id ? 'scale-105' : ''
                }`}
              >
                {/* Food Image Container */}
                <div className={`relative mb-2 sm:mb-3 transition-all duration-500 ${
                  activeCategory === category.id ? 'animate-float' : 'group-hover:animate-float'
                }`}>
                  {/* Subtle Shadow/Glow */}
                  <div className={`absolute inset-0 rounded-full blur-xl transition-all duration-500 ${
                    activeCategory === category.id 
                      ? 'bg-red-500/20 scale-150' 
                      : 'bg-white/5 scale-100 group-hover:bg-red-500/15 group-hover:scale-125'
                  }`}></div>
                  
                  {/* Main Food Image */}
                  <div className={`relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full overflow-hidden transition-all duration-500 ${
                    activeCategory === category.id
                      ? 'ring-3 ring-red-500 ring-opacity-60 shadow-xl shadow-red-500/30'
                      : 'ring-2 ring-gray-600 group-hover:ring-red-400 group-hover:ring-opacity-50 group-hover:shadow-lg group-hover:shadow-red-400/20'
                  }`}>
                    <img 
                      src={category.image}
                      alt={category.name}
                      className={`w-full h-full object-cover transition-all duration-500 ${
                        activeCategory === category.id 
                          ? 'scale-110 brightness-110 saturate-110' 
                          : 'scale-100 group-hover:scale-110 group-hover:brightness-110 group-hover:saturate-110'
                      }`}
                    />
                    
                    {/* Overlay for better contrast */}
                    <div className={`absolute inset-0 transition-all duration-500 ${
                      activeCategory === category.id
                        ? 'bg-red-600/10'
                        : 'bg-gray-900/10 group-hover:bg-red-600/5'
                    }`}></div>
                  </div>
                  
                  {/* Active Pulse Ring */}
                  {activeCategory === category.id && (
                    <div className="absolute inset-0 rounded-full border-2 border-red-500 animate-ping opacity-60"></div>
                  )}
                </div>
                
                {/* Category Label */}
                <div className="text-center">
                  <h3 className={`text-xs sm:text-sm lg:text-base font-bold tracking-wider transition-all duration-300 ${
                    activeCategory === category.id ? 'text-red-400' : 'text-white group-hover:text-red-300'
                  }`}>
                    {category.name}
                  </h3>
                </div>
                
                {/* Selection Indicator Dot */}
                <div className={`mt-1 sm:mt-2 w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full transition-all duration-300 ${
                  activeCategory === category.id 
                    ? 'bg-red-500 scale-100 shadow-lg shadow-red-500/50' 
                    : 'bg-transparent scale-0 group-hover:bg-red-400 group-hover:scale-75'
                }`}></div>
                
                {/* Hover Shimmer Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Menu Items */}
        {/* Pizza Pricing Info */}
        {activeCategory === 'pizza' && (
          <div className="mb-8 sm:mb-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-600/30 rounded-2xl p-4 sm:p-8 text-center">
              <h3 className="text-lg sm:text-2xl font-bold text-white mb-6">
                Pizza Pricing (Medium 12" | Large 15" | XL 18")
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Classic Pizzas */}
                <div className="bg-gray-800/60 rounded-2xl p-4 border border-red-500/20 shadow-lg">
                  <h4 className="text-lg font-bold text-orange-400 mb-3">Classic Pizzas</h4>
                  <div className="flex justify-around items-center">
                    <div>
                      <div className="text-xl font-bold text-white">$19.99</div>
                      <div className="text-xs text-gray-400">Medium 12"</div>
                    </div>
                    <div className="h-8 w-[1px] bg-gray-700"></div>
                    <div>
                      <div className="text-xl font-bold text-white">$24.99</div>
                      <div className="text-xs text-gray-400">Large 15"</div>
                    </div>
                    <div className="h-8 w-[1px] bg-gray-700"></div>
                    <div>
                      <div className="text-xl font-bold text-white">$29.99</div>
                      <div className="text-xs text-gray-400">XL 18"</div>
                    </div>
                  </div>
                </div>

                {/* Signature Pizzas */}
                <div className="bg-gray-800/60 rounded-2xl p-4 border border-red-500/20 shadow-lg">
                  <h4 className="text-lg font-bold text-orange-400 mb-3">Signature Pizzas</h4>
                  <div className="flex justify-around items-center">
                    <div>
                      <div className="text-xl font-bold text-white">$21.99</div>
                      <div className="text-xs text-gray-400">Medium 12"</div>
                    </div>
                    <div className="h-8 w-[1px] bg-gray-700"></div>
                    <div>
                      <div className="text-xl font-bold text-white">$26.99</div>
                      <div className="text-xs text-gray-400">Large 15"</div>
                    </div>
                    <div className="h-8 w-[1px] bg-gray-700"></div>
                    <div>
                      <div className="text-xl font-bold text-white">$31.99</div>
                      <div className="text-xs text-gray-400">XL 18"</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footnotes / Substitutes */}
              <div className="mt-6 pt-6 border-t border-red-600/20 flex flex-wrap justify-center gap-4 text-xs sm:text-sm text-gray-300">
                <span className="flex items-center gap-1.5 bg-gray-800/40 px-3 py-1 rounded-full border border-green-500/30">
                  <Leaf className="h-3.5 w-3.5 text-green-400" />
                  Gluten Friendly Crust: <strong className="text-white">+$3.5</strong> (Medium only)
                </span>
                <span className="flex items-center gap-1.5 bg-gray-800/40 px-3 py-1 rounded-full border border-green-500/30">
                  <Leaf className="h-3.5 w-3.5 text-green-400" />
                  Vegan Cheese: <strong className="text-white">+$3.5</strong>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Pasta Pricing Info */}
        {activeCategory === 'pasta' && (
          <div className="mb-8 sm:mb-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-600/30 rounded-2xl p-4 sm:p-8 text-center">
              <h3 className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                Fresh Pastas
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 max-w-2xl mx-auto">
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-4xl font-bold text-green-400 mb-1 sm:mb-2">$12.00</div>
                  <div className="text-white font-semibold mb-1">Base Pastas</div>
                  <div className="text-gray-300 text-xs sm:text-sm">Four Cheese Baked Spaghetti & Cheese Tortellini</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-xl sm:text-3xl font-bold text-blue-400 mb-1 sm:mb-2">$14.00</div>
                  <div className="text-white font-semibold mb-1">With Meat</div>
                  <div className="text-gray-300 text-xs sm:text-sm">Includes meatballs, chicken, or Italian sausage</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Salads Pricing Info */}
        {activeCategory === 'salads' && (
          <div className="mb-8 sm:mb-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-600/30 rounded-2xl p-4 sm:p-8 text-center">
              <h3 className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                Fresh Salads - Add Chicken to Any Salad!
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 max-w-2xl mx-auto">
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-4xl font-bold text-green-400 mb-1 sm:mb-2">$10.00</div>
                  <div className="text-white font-semibold mb-1">Regular Salads</div>
                  <div className="text-gray-300 text-xs sm:text-sm">Fresh ingredients and house dressings</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-xl sm:text-3xl font-bold text-blue-400 mb-1 sm:mb-2">+$2.00</div>
                  <div className="text-white font-semibold mb-1">Add Chicken</div>
                  <div className="text-gray-300 text-xs sm:text-sm">Grilled chicken breast</div>
                </div>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm mt-3 sm:mt-4">
                Served with your choice of dressing
              </p>
            </div>
          </div>
        )}

        {/* Sides/Appetizers Pricing Info */}
        {activeCategory === 'appetizers' && (
          <div className="mb-8 sm:mb-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-600/30 rounded-2xl p-4 sm:p-8 text-center">
              <h3 className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                Sides & Appetizers
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-xl sm:text-3xl font-bold text-yellow-400 mb-1 sm:mb-2">$8 | $14</div>
                  <div className="text-white font-semibold mb-1">Chicken Wings</div>
                  <div className="text-gray-300 text-xs sm:text-sm">6 pcs | 12 pcs</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-xl sm:text-3xl font-bold text-orange-400 mb-1 sm:mb-2">$8.00</div>
                  <div className="text-white font-semibold mb-1">Garlic bites</div>
                  <div className="text-gray-300 text-xs sm:text-sm">With marinara sauce</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4">
                  <div className="text-xl sm:text-3xl font-bold text-red-400 mb-1 sm:mb-2">$9.00</div>
                  <div className="text-white font-semibold mb-1">Cheese Garlic Breadsticks</div>
                  <div className="text-gray-300 text-xs sm:text-sm">With marinara sauce</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {filteredItems.map((item, index) => (
            <div 
              key={item.id}
              className="group bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={item.image}
                  alt={item.name}
                  className="w-full h-40 sm:h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 flex space-x-2">
                  {item.glutenFree && (
                    <div className="bg-green-600/90 backdrop-blur-sm p-1.5 sm:p-2 rounded-full">
                      <Leaf className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                    </div>
                  )}
                  <button className="bg-white/20 backdrop-blur-sm p-1.5 sm:p-2 rounded-full hover:bg-red-600 transition-colors duration-200">
                    <Heart className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                  </button>
                </div>
                {item.glutenFree && (
                  <div className="absolute bottom-3 left-3 bg-green-600/90 backdrop-blur-sm px-2 py-1 rounded-full">
                    <span className="text-white text-xs font-medium">Gluten-Free</span>
                  </div>
                )}
              </div>
              
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{item.name}</h3>
                <p className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">{item.description}</p>
                
                <div className={`flex items-center justify-between mb-3 sm:mb-4 ${
                  ''
                }`}>
                  <div className="text-xl sm:text-2xl font-bold text-red-600">
                    {item.sizes ? (
                      <div className="text-center">
                        <span className="text-lg text-gray-300 font-normal">Starting at</span>
                        <div className="text-2xl font-bold text-red-600">${item.sizes.medium}</div>
                      </div>
                    ) : (
                      typeof item.basePrice === 'number' ? `$${item.basePrice.toFixed(2)}` : item.basePrice
                    )}
                  </div>
                  {!item.sizes && (
                    <div className="flex items-center space-x-1 sm:space-x-2 text-gray-400 text-xs sm:text-sm">
                      <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>15-20 min</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Menu;